import React, { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  TablePagination,
} from "@mui/material";
import { TypographyNormal } from "../customComponent";
import { getViewers } from "../../store/actions/viewerAction";
import ViewerItem from "./ViewerItem";
import { colorStyling } from "../../helpers/color";
import { filterObjtoString } from "../../helpers/filterHelper";
import {
  SET_LOADING_VIEWER,
  SET_VIEWER_PAGE,
  SET_VIEWER_ROWS_PER_PAGE,
} from "../../store/actionTypes/viewerActionTypes";
import { parseVariables } from "../../helpers/localize";

const ViewerList = () => {
  const dispatch = useDispatch();
  const { viewers, viewerFilters, loadingViewer, page, rowsPerPage } =
    useSelector((state) => state.viewer);

  const { t: tCommon } = useTranslation();

  const scrollRef = useRef(null);

  const handleLoading = (value) => {
    dispatch({ type: SET_LOADING_VIEWER, payload: value });
  };

  const handleChangePagination = (e, page) => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
    dispatch({ type: SET_VIEWER_PAGE, payload: page });
  };

  const handleChangeRowsPerPage = (event) => {
    dispatch({
      type: SET_VIEWER_ROWS_PER_PAGE,
      payload: parseInt(event.target.value, 10),
    });
    handleChangePagination(null, 0);
  };

  const fetchData = async (filterObj) => {
    let isPrefetch = false;
    let limit = rowsPerPage;
    let offset = page * rowsPerPage;
    if (offset === 0) {
      limit = limit * 2; //fetch 2 page worth of data
      isPrefetch = false;
    } else {
      offset = (page + 1) * rowsPerPage;
      isPrefetch = true;
    }
    let filterString = filterObjtoString(filterObj, limit, offset);

    const viewersLength = viewers?.items?.length;
    if (viewersLength === 0) handleLoading(true);
    if (
      viewersLength !== viewers.totalItem ||
      viewersLength === 0 ||
      offset === 0
    )
      dispatch(getViewers(filterString, isPrefetch, handleLoading));
  };

  useEffect(() => {
    fetchData(viewerFilters);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  useEffect(() => {
    if (page === 0) {
      fetchData(viewerFilters);
    } else handleChangePagination(null, 0);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewerFilters]);

  const displayedRowsLabel = ({ from, to, count }) => {
    return parseVariables(tCommon("table.displayedRowsLabel"), {
      prefix: `${from}–${to}`,
      suffix:
        count !== -1
          ? count
          : `${tCommon("table.displayedRowsMoreThan")} ${to}`,
    });
  };

  if (loadingViewer)
    return (
      <Box
        sx={{ height: "20vh" }}
        display="flex"
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
      >
        <CircularProgress
          size={22}
          thickness={3}
          sx={{ color: colorStyling.primary }}
        />
        <TypographyNormal
          sx={{ color: colorStyling.primary, mt: 2, fontWeight: 300 }}
        >
          {tCommon("loadingDataList")}
        </TypographyNormal>
      </Box>
    );

  return (
    <>
      <Box>
        {loadingViewer ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            sx={{ height: "20vh" }}
          >
            <CircularProgress
              size={20}
              thickness={3}
              sx={{ color: colorStyling.primary }}
            />
            <TypographyNormal
              sx={{ color: colorStyling.primary, mt: 2, fontWeight: 300 }}
            >
              {tCommon("loadingDataList")}
            </TypographyNormal>
          </Box>
        ) : (
          <>
            {!viewers?.items?.length ? (
              <Paper elevation={2} sx={{ borderRadius: "6px", p: 2 }}>
                <Alert severity="info" variant="outlined">
                  <strong>{tCommon("noData")}</strong>
                </Alert>
              </Paper>
            ) : (
              <Paper
                component="section"
                elevation={2}
                sx={{ p: 2, my: 2, borderRadius: "6px" }}
              >
                <Box ref={scrollRef} sx={{ overflowY: "auto", maxHeight: 500 }}>
                  {viewers?.items
                    ?.slice(page * rowsPerPage, (page + 1) * rowsPerPage)
                    ?.map((viewer) => (
                      <ViewerItem
                        key={viewer.id}
                        viewer={viewer}
                        dispatch={dispatch}
                      />
                    ))}
                </Box>
              </Paper>
            )}
          </>
        )}
      </Box>

      {(!loadingViewer || viewers?.totalItem > 0) && (
        <Box
          sx={{
            pt: 2,
            pb: 1,
            pr: 1,
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Paper component="div" elevation={1} sx={{ px: 1 }}>
            <TablePagination
              component="div"
              count={viewers.totalItem}
              page={page}
              onPageChange={handleChangePagination}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelDisplayedRows={displayedRowsLabel}
              labelRowsPerPage={tCommon("table.rowsPerPageLabel")}
            />
          </Paper>
        </Box>
      )}
    </>
  );
};

export default ViewerList;
